import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';
import { applyModuleSeo } from '../../utils/seo';

const BusinessConsultantLanding: React.FC = () => {
  useEffect(() => {
    const title = 'AI Business Consultant | Explanatum';
    const desc = 'An always-on AI consultant that remembers your business, surfaces live CRM insights, and executes actions like LinkedIn posting—without you leaving the chat.';
    const url = 'https://explanatum.co.uk/modules/business-consultant';
    applyModuleSeo({
      title,
      description: desc,
      canonicalUrl: url,
      keywords: [
        'ai business consultant',
        'virtual strategy advisor',
        'automated business insights',
        'crm ai assistant',
        'explanatum business ai'
      ],
      structuredDataId: 'ld-module-business-consultant',
      softwareName: 'AI Business Consultant',
      softwareCategory: 'BusinessApplication'
    });
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch (err) {
      console.warn('Analytics unavailable', err);
    }
  };

  return (
    <main className="bg-white" role="main">
      {/* Hero */}
      <section aria-label="AI Business Consultant overview" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1 text-sm font-semibold text-blue-700">
                <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                AI Business Consultant
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Your business remembers everything.
                <span className="mt-2 block text-gray-900">
                  So should your advisor.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Turn every chat into progress. The Explanatum AI Business Consultant recalls past decisions, reads your live metrics, and carries out tasks like LinkedIn posts or CRM updates—without you leaving the conversation.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#pricing"
                  onClick={() => track('click_consultant_cta', { placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 transition-colors px-6 py-3 text-base font-semibold text-white shadow-lg transition hover:scale-[1.02]"
                >
                  Start free trial
                </a>
                <a
                  href="#compare"
                  className="inline-flex items-center justify-center rounded-full border border-slate-500/50 px-6 py-3 text-base font-semibold text-gray-600 hover:bg-blue-50 transition-colors"
                >
                  See how it compares
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  Remembers milestones & commitments
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-sky-400" />
                  Connects to CRM, marketing & finance
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  Executes tasks directly in chat
                </span>
              </div>
            </div>
            <aside className="relative">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-blue-50 blur-3xl opacity-40" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-cyan-50 blur-3xl opacity-30" aria-hidden="true" />
              <div className="relative rounded-3xl border border-gray-200 bg-white p-6 shadow-2xl backdrop-blur">
                <div className="flex items-center justify-between text-xs uppercase tracking-widest text-gray-500">
                  <span>Timeline</span>
                  <span>Live feed</span>
                </div>
                <div className="mt-5 space-y-4">
                  {[{
                    title: 'Revenue milestone logged',
                    detail: 'Closed £15K ARR with Acme Corp',
                    tag: 'Milestone',
                    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                    time: '1h ago'
                  }, {
                    title: 'Pipeline update',
                    detail: '18% uplift WoW. 3 deals at risk flagged.',
                    tag: 'CRM',
                    color: 'bg-sky-50 text-sky-700 border-sky-200',
                    time: '3h ago'
                  }, {
                    title: 'Action executed',
                    detail: 'LinkedIn post published for launch campaign.',
                    tag: 'LinkedIn',
                    color: 'bg-blue-50 text-blue-700 border-blue-200',
                    time: 'Today'
                  }].map((item, idx) => (
                    <div key={item.title} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-inner">
                      <div className="flex items-center justify-between">
                        <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${item.color}`}>
                          {item.tag}
                        </span>
                        <span className="text-xs text-gray-500">{item.time}</span>
                      </div>
                      <h3 className="mt-3 text-sm font-semibold text-gray-600">{item.title}</h3>
                      <p className="mt-1 text-sm text-gray-500">{item.detail}</p>
                      {idx === 1 && (
                        <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
                          <p className="text-xs font-semibold text-emerald-700">Suggested next step</p>
                          <p className="mt-1 text-sm text-gray-600">Ping Sarah and attach the revised proposal.</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="mt-5 text-xs text-gray-500">All synced with your Explanatum workspace & WhatsApp threads.</p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section aria-label="Key benefits" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Why teams rely on the AI Business Consultant</h2>
            <p className="mt-4 text-base text-gray-500">It combines strategic memory, live data, and hands-on execution so momentum never stalls.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[{
              title: 'Remembers every conversation',
              body: 'Past decisions, milestones, and follow-ups surface instantly. Ask “What did we decide about pricing?” and get the exact answer plus context.'
            }, {
              title: 'Understands your metrics',
              body: 'Connect CRM, marketing, and finance. The consultant turns dashboards into plain-language insights and alerts you before issues escalate.'
            }, {
              title: 'Executes for you',
              body: 'Publish a LinkedIn update, log a closed deal, email an investor, or assign a follow-up. Just ask—it completes the task and confirms what changed.'
            }, {
              title: 'Works across channels',
              body: 'Pick up the same conversation on desktop, mobile, or WhatsApp. Every action, reply, and note stays synced across your team.'
            }, {
              title: 'Keeps goals visible',
              body: 'Track launch plans, revenue targets, or hiring goals. The consultant reminds owners, nudges blockers, and celebrates wins.'
            }, {
              title: 'Fits your workflow',
              body: 'Exports summaries, syncs with documents, and gives stakeholders clear updates without lengthy meetings or status decks.'
            }].map((item) => (
              <article key={item.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Why it beats generic AI chatbots</h2>
            <p className="mt-4 text-base text-gray-500">ChatGPT, Copilot, and Gemini stop at clever answers. The consultant keeps going until work is done.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[{
              title: 'AI Business Consultant',
              points: [
                'Retains your business memory automatically',
                'Reads live CRM, marketing, and finance data',
                'Executes actions (publish, log, assign) inside chat',
                'Shared workspace with audit trails for your team',
                'Available in the app and via WhatsApp'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'ChatGPT & Google Gemini',
              points: [
                'No memory of your context unless re-prompted',
                'Cannot securely access business systems',
                'Suggestions only—execution is manual',
                'Single-user experience with no shared history',
                'Lives outside your daily workflow'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Microsoft Copilot & similar',
              points: [
                'Optimised for Microsoft 365 co-authoring, not end-to-end operations',
                'No direct access to your CRM or go-to-market metrics',
                'Provides suggestions only—cannot execute actions for you',
                'Lives inside Office apps rather than your business systems',
                'Designed for individual productivity, not shared team memory'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }].map((column) => (
              <article key={column.title} className={`rounded-3xl border ${column.accent} p-6 shadow-xl shadow-lg`}>
                <h3 className="text-lg font-semibold text-gray-900">{column.title}</h3>
                <ul className="mt-5 space-y-3 text-sm text-gray-900">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-gray-900">
                      <span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                      <span className="text-gray-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="mt-14 rounded-3xl border border-gray-200 bg-sky-400/10 p-8 text-center backdrop-blur">
            <h3 className="text-2xl font-semibold text-gray-900">“How is our pipeline trending and what should we do next?”</h3>
            <p className="mt-3 text-base text-gray-600">
              The consultant replies with real metrics, highlights at-risk deals, drafts the outreach, and can send it on your behalf.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#pricing"
                onClick={() => track('click_consultant_cta', { placement: 'compare' })}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-sky-500 via-indigo-500 to-purple-500 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-sky-500/30 hover:scale-[1.02]"
              >
                Run a pilot
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center rounded-full border border-slate-500/60 px-6 py-3 text-base font-semibold text-gray-600 hover:border-slate-300/70"
              >
                Book a live walkthrough
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section aria-label="Real scenarios" className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">In minutes you move from idea to execution</h2>
            <p className="mt-4 text-base text-gray-500">Select a scenario, ask for help, and the consultant delivers the plan, assets, and logged actions.</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {[{
              title: 'Weekly operating review',
              body: '“Summarise this week’s performance.” You get revenue highlights, pipeline shifts, campaign results, and suggested next steps.'
            }, {
              title: 'Investor-ready update',
              body: '“Draft an investor update around our £15K ARR win.” It compiles the story, attaches key metrics, and logs the send in CRM.'
            }, {
              title: 'Launch momentum',
              body: '“Plan our product tier launch.” The consultant builds the timeline, assigns owners, drafts LinkedIn and email assets, and schedules reminders.'
            }].map((scenario) => (
              <article key={scenario.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{scenario.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{scenario.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <PricingSection />
        </div>
      </section>
    </main>
  );
};

export default BusinessConsultantLanding;
